/**
 * Page « Notre mission » : révélations, compteurs, chronologie, parallaxe.
 *
 * Le CSS décrit l'état FINAL. Ce module pose la classe `is-pre` (état de
 * départ) puis la retire à l'entrée dans le champ. Si ce fichier ne s'exécute
 * pas (script bloqué, erreur, navigateur ancien), la classe n'est jamais
 * posée et la page s'affiche complète.
 *
 * Un seul IntersectionObserver pour toute la page, une seule boucle
 * requestAnimationFrame pour la parallaxe et le tracé de l'axe. Le
 * gestionnaire de défilement ne fait que lever un drapeau : il ne calcule
 * rien et ne touche jamais au DOM.
 */

const PRE = 'is-pre';
const THRESHOLD = 0.25;
const COUNT_MS = 1600;
const NNBSP = ' '; // espace insécable étroite : « 1 550 », jamais « 1,550 »

const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

/* =========================================================================
   Garde-fou

   Règle : une animation qui échoue doit rendre la page VISIBLE, jamais
   l'inverse. `is-pre` met `opacity: 0` ; tout ce qui empêche son retrait
   laisse du contenu invisible pour toujours, alors que le HTML est entier.
   Trois filets, du plus précis au plus grossier.
   ========================================================================= */

let renonce = false;

/** Retire `is-pre` partout et interdit toute nouvelle pose. */
function toutAfficher(cause) {
  renonce = true;
  try {
    document.querySelectorAll('.' + PRE).forEach((el) => el.classList.remove(PRE));
    // Un axe de chronologie laissé à zéro donnerait une frise inachevée.
    document.querySelectorAll('[data-lrs-axis-fill]').forEach((el) => {
      el.style.height = '100%';
    });
    document.querySelectorAll('[data-lrs-count]').forEach((el) => {
      try {
        runCounter(el);
      } catch (_) {
        /* un compteur cassé ne doit pas empêcher les autres de s'afficher */
      }
    });
  } catch (_) {
    /* dernier recours : ne rien laisser remonter */
  }
  if (cause) console.warn('[LRS] animation abandonnée, contenu rendu visible :', cause);
}

/** Enveloppe une fonction : au moindre jet, la page redevient visible. */
function sansCasse(fn) {
  return function wrapped(...args) {
    if (renonce) return undefined;
    try {
      return fn.apply(this, args);
    } catch (error) {
      toutAfficher(error);
      return undefined;
    }
  };
}

/**
 * Le visiteur a-t-il déjà atteint cet élément ?
 *
 * C'est la garde qui compte le plus. Ces modules sont différés : sur une
 * connexion lente, ils s'exécutent APRÈS que le visiteur a commencé à
 * défiler. Masquer alors un élément déjà dépassé est sans retour, parce
 * qu'un IntersectionObserver ne se déclenche qu'à l'ENTRÉE dans le champ et
 * jamais pour ce qui est déjà passé. La page s'arrête alors en cours de
 * route, HTML complet à l'appui.
 */
function dejaAtteint(el) {
  const haut = window.innerHeight || document.documentElement.clientHeight || 0;
  return el.getBoundingClientRect().top < haut;
}

/**
 * Cet élément aurait-il dû être révélé ?
 *
 * On rejoue la condition de l'observateur plutôt qu'une plus lâche : révéler
 * dès le premier pixel supprimerait l'animation au lieu de la sauver. Seule
 * exception, un élément entièrement dépassé : plus personne ne le révélera.
 */
function auraitDuEtreVu(el) {
  const r = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight || 0;
  if (r.bottom < 0) return true;
  // Les lignes de chronologie n'obéissent pas à la visibilité mais à l'axe.
  if (el.dataset.lrsTimelineItem !== undefined) return false;
  if (!r.height) return false;
  const visible = Math.min(r.bottom, vh) - Math.max(r.top, 0);
  return visible / r.height >= THRESHOLD;
}

/**
 * Balayage de secours, indépendant de l'observateur.
 *
 * Il ne change rien quand tout va bien : l'observateur a déjà retiré la
 * classe avant que le balayage n'y arrive. Il couvre le cas où l'observateur
 * n'a jamais vu passer l'élément, typiquement un module arrivé après le
 * défilement du visiteur.
 */
function filet() {
  const restants = document.querySelectorAll('.' + PRE);
  if (!restants.length) return;
  restants.forEach((el) => {
    if (dejaAtteint(el) && auraitDuEtreVu(el)) revealNow(el);
  });
}

// Une erreur levée hors de nos enveloppes (module tiers, navigateur ancien)
// ne doit pas non plus laisser la page coupée.
window.addEventListener('error', (event) => {
  if (renonce) return;
  const src = String((event && event.filename) || '');
  if (src.includes('/lrs-')) toutAfficher(event.message);
});

/* ------------------------------------------------------------ révélations */

let observer = null;

function revealNow(el) {
  el.classList.remove(PRE);
  if (el.dataset.lrsCount !== undefined) runCounter(el);
}

function armReveals(root) {
  const items = Array.from(root.querySelectorAll('[data-lrs-reveal]'));
  if (!items.length) return;

  if (reduced.matches || !('IntersectionObserver' in window)) return;

  observer ||= new IntersectionObserver(
    sansCasse((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        revealNow(entry.target);
        // once : la séquence ne se rejoue pas au retour.
        observer.unobserve(entry.target);
      });
    }),
    { threshold: THRESHOLD }
  );

  items.forEach((el) => {
    if (el.dataset.lrsArmed === 'on') return;
    el.dataset.lrsArmed = 'on';

    // Déjà atteint : on n'a plus le droit de le masquer. Voir `dejaAtteint`.
    if (dejaAtteint(el)) {
      revealNow(el);
      return;
    }

    // La chronologie pilote ses propres lignes : l'axe décide quand elles
    // apparaissent, pas le simple fait d'être visible.
    if (el.dataset.lrsTimelineItem !== undefined) {
      el.classList.add(PRE);
      return;
    }
    el.classList.add(PRE);
    observer.observe(el);
  });
}

/* -------------------------------------------------------------- compteurs */

function formatNumber(value, decimals) {
  const fixed = Number(value).toFixed(decimals);
  const [whole, frac] = fixed.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, NNBSP);
  return frac ? `${grouped},${frac}` : grouped;
}

const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

function runCounter(el) {
  const out = el.querySelector('[data-lrs-count-value]') || el;
  const target = parseFloat(el.dataset.lrsCount || '0');
  const decimals = parseInt(el.dataset.lrsCountDecimals || '0', 10);
  // Durée par défaut 1600 ms. L'attribut permet de ralentir la montée, ce qui
  // sert à la photographier en cours de route sans toucher à la logique.
  const duration = parseInt(el.dataset.lrsCountMs || '', 10) || COUNT_MS;

  // Un compteur qui va de 0 à 0 ne fait rien : le chiffre apparaît en fondu
  // avec le reste du bloc, comme demandé.
  if (!Number.isFinite(target) || target === 0 || reduced.matches) {
    out.textContent = formatNumber(target || 0, decimals);
    return;
  }

  const started = performance.now();

  const step = (now) => {
    const t = Math.min(1, (now - started) / duration);
    out.textContent = formatNumber(target * easeOutExpo(t), decimals);
    if (t < 1) requestAnimationFrame(step);
    else out.textContent = formatNumber(target, decimals);
  };

  out.textContent = formatNumber(0, decimals);
  requestAnimationFrame(step);
}

/* ------------------------------------------------ chronologie et parallaxe */

/** @type {{el: HTMLElement, amp: number}[]} */
let parallaxItems = [];
/** @type {{root: HTMLElement, fill: HTMLElement, rows: {el: HTMLElement, offset: number}[], note: HTMLElement | null}[]} */
let timelines = [];
let ticking = false;

const measureTimelines = sansCasse(function measureTimelines() {
  timelines.forEach((tl) => {
    if (!tl.root || !tl.root.isConnected) return;
    const rootTop = tl.root.getBoundingClientRect().top;
    tl.rows.forEach((row) => {
      const dot = row.el.querySelector('[data-lrs-dot]');
      const box = (dot || row.el).getBoundingClientRect();
      row.offset = box.top + box.height / 2 - rootTop;
    });
  });
});

const frame = sansCasse(function frame() {
  ticking = false;
  const vh = window.innerHeight || 1;

  // Filet à chaque tour : coûte un `querySelectorAll` qui ne renvoie plus
  // rien dès que la page est révélée.
  filet();

  for (const item of parallaxItems) {
    // Un élément détaché n'a plus de parent : l'éditeur de thème remplace
    // des sections entières sans prévenir.
    const parent = item.el.parentElement;
    if (!parent) continue;
    const rect = parent.getBoundingClientRect();
    if (rect.bottom < -200 || rect.top > vh + 200) continue;
    // 0 quand la section entre par le bas, 1 quand elle sort par le haut.
    const p = Math.min(1, Math.max(0, (vh - rect.top) / (vh + rect.height)));
    item.el.style.setProperty('--lrs-par', `${(p * item.amp).toFixed(1)}px`);
  }

  for (const tl of timelines) {
    if (!tl.root || !tl.fill) continue;
    const rect = tl.root.getBoundingClientRect();
    const height = tl.root.offsetHeight || 1;
    // L'axe commence à se tracer quand le haut de la frise passe les 72 %
    // de la fenêtre, et finit quand son bas les a franchis.
    const drawn = Math.min(1, Math.max(0, (vh * 0.72 - rect.top) / height));
    tl.fill.style.height = `${(drawn * 100).toFixed(2)}%`;

    const reached = drawn * height;
    let allDone = true;
    for (const row of tl.rows) {
      if (row.el.classList.contains(PRE)) {
        if (reached >= row.offset - 10) row.el.classList.remove(PRE);
        else allDone = false;
      }
    }
    // Le bloc de précision politique arrive après toutes les lignes.
    if (allDone && tl.note) tl.note.classList.remove(PRE);
  }
});

function onScroll() {
  if (renonce || ticking) return;
  ticking = true;
  requestAnimationFrame(frame);
}

const armMotion = sansCasse(function armMotion(root) {
  if (reduced.matches) return;

  parallaxItems = parallaxItems.concat(
    Array.from(root.querySelectorAll('[data-lrs-parallax]'))
      .filter((el) => el.dataset.lrsArmed !== 'on')
      .map((el) => {
        el.dataset.lrsArmed = 'on';
        return { el, amp: parseFloat(el.dataset.lrsParallax || '30') };
      })
  );

  timelines = timelines.concat(
    Array.from(root.querySelectorAll('[data-lrs-timeline]'))
      .filter((el) => el.dataset.lrsArmed !== 'on')
      .map((el) => {
        el.dataset.lrsArmed = 'on';
        return {
          root: el,
          fill: el.querySelector('[data-lrs-axis-fill]'),
          rows: Array.from(el.querySelectorAll('[data-lrs-timeline-item]')).map((r) => ({
            el: r,
            offset: 0,
          })),
          note: el.parentElement?.querySelector('[data-lrs-timeline-note]') || null,
        };
      })
      .filter((tl) => {
        // Sans axe, personne ne révélera jamais les lignes : `armReveals` les
        // a déjà mises en `is-pre` et c'est l'axe qui devait les rendre. On
        // les affiche tout de suite plutôt que de les perdre.
        if (!tl.fill) {
          tl.rows.forEach((row) => revealNow(row.el));
          return false;
        }
        return true;
      })
  );

  if (!parallaxItems.length && !timelines.length) return;

  measureTimelines();
  onScroll();
});

/* ------------------------------------------------------------------ mise en route */

const scan = sansCasse(function scan(root) {
  const scope = root || document;
  armReveals(scope);
  armMotion(scope);
});

scan(document);

// Au-dessus de 990 px, Horizon ne fait pas défiler la fenêtre mais
// `.page-wrapper`. Un évènement `scroll` d'élément ne remonte pas jusqu'à
// `window` : l'écoute se fait donc en phase de capture sur le document, qui
// voit passer le défilement quel que soit le conteneur.
document.addEventListener('scroll', onScroll, { capture: true, passive: true });
window.addEventListener('scroll', onScroll, { passive: true });
window.addEventListener(
  'resize',
  () => {
    measureTimelines();
    onScroll();
  },
  { passive: true }
);

// Les polices et les images changent la hauteur de la frise après coup.
window.addEventListener('load', () => {
  measureTimelines();
  onScroll();
});

// L'éditeur de thème re-rend une section : il faut ré-armer la nouvelle copie.
document.addEventListener('shopify:section:load', (event) => scan(event.target));

/* ------------------------------------------------------------------ filets
   Deux passages de rattrapage. Ils ne changent rien quand tout va bien : à ce
   moment-là il ne reste plus rien à l'écran qui porte encore `is-pre`. Ils
   servent au cas dégradé, celui où l'observateur n'a pas vu passer un élément
   parce que le module est arrivé trop tard. */
setTimeout(sansCasse(filet), 2500);
window.addEventListener('load', () => setTimeout(sansCasse(filet), 1500));

// Retour par l'historique : la page sort du cache avec ses classes d'alors.
window.addEventListener('pageshow', (event) => {
  if (event.persisted) sansCasse(filet)();
});
